/**
 * AVPlay - Samsung's native player, and the only one that decodes video on a
 * Tizen panel (TIZEN-16).
 *
 * ## Why this exists
 *
 * A `<video src>` on the HG/QB panels we ship to does not play. Three files
 * were tested on hardware - a 1080x1920 original, a 1920x1080 re-encode, and a
 * minimal 1280x720 Constrained Baseline clip with no audio at 1.5Mbps - and
 * all three parked at 0.1s with the buffer FULL and the network idle. That is
 * not a delivery fault and not an encoding fault: the element fetched every
 * byte and then declined to decode them.
 *
 * `nenda-autoplayer` never hit this because on Tizen it never uses the HTML
 * element at all. Its player matrix sends linear channels to Shaka and every
 * other video to AVPlay, and it has been playing signage on these panels in
 * production for years. This module is that path, ported - not reinvented.
 *
 * ## The one fact that shapes everything below
 *
 * AVPlay is a SINGLE native surface. There is one `<object id="avplayer">`, one
 * decoder, one playhead. Every design in the HTML world that assumes you can
 * hold two elements - the layer pair that pre-rolls the next clip behind the
 * current one, a hidden twin to hand over to - is unavailable here. So the
 * caller plays one thing at a time, and the gap between clips is covered by
 * {@link preloadNext}, which opens and prepares the next source in the moment
 * after the current one completes rather than alongside it.
 *
 * ## Errors are reported, never thrown
 *
 * Every firmware call is individually guarded. Not defensive habit: these
 * methods genuinely differ between panel generations, `setLooping` and
 * `setStreamingProperty` are absent on some, and a throw from any one of them
 * inside a React effect takes the whole surface down. A wall that lost its
 * rotation hint is a wall that is still playing.
 */

/* -------------------------------------------------------------------------- */
/* The firmware surface, as much of it as we use.                             */
/* -------------------------------------------------------------------------- */

/**
 * Every method optional, because they genuinely are. The panels differ by
 * generation and firmware, and calling through `?.()` at each site is the only
 * honest way to describe a namespace whose shape is discovered at runtime.
 */
interface AvPlayApi {
  open?(url: string): void;
  close?(): void;
  play?(): void;
  stop?(): void;
  pause?(): void;
  prepareAsync?(ok: () => void, fail: (e: unknown) => void): void;
  seekTo?(ms: number): void;
  getState?(): string;
  getCurrentTime?(): number;
  getDuration?(): number;
  setListener?(listener: Record<string, unknown>): void;
  setDisplayRect?(x: number, y: number, w: number, h: number): void;
  setDisplayMethod?(method: string): void;
  setDisplayRotation?(rotation: string): void;
  setLooping?(loop: boolean): void;
  enableAudioStream?(): void;
  disableAudioStream?(): void;
  setBufferingParam?(kind: string, unit: string, value: number): void;
  setStreamingProperty?(key: string, value: string): void;
  setDrm?(type: string, operation: string, param: string): string | undefined;
  getTotalTrackInfo?(): Array<{
    type?: string;
    width?: number;
    height?: number;
  }>;
}

function api(): AvPlayApi | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as { webapis?: { avplay?: AvPlayApi } };
  return w.webapis?.avplay ?? null;
}

/**
 * Can this panel play video through AVPlay?
 *
 * Checks for `open` specifically rather than the namespace: `webapis.avplay`
 * exists as an empty object on some builds, and a truthy namespace with no
 * methods would route every clip into a player that cannot open one - which is
 * a blacker screen than falling back to the element that at least tries.
 */
export function avplayAvailable(): boolean {
  return typeof api()?.open === "function";
}

/** The id of the native surface. Must match the element {@link AvPlaySurface} renders. */
export const AVPLAY_ELEMENT_ID = "avplayer";

function surface(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  return document.getElementById(AVPLAY_ELEMENT_ID);
}

/**
 * Show/hide the native surface.
 *
 * `visibility`, never `display` and never unmounting: the surface is a hole
 * punched through the browser's compositor to a hardware plane, and removing
 * the element from the DOM tears that plane down. Hiding it leaves the plane
 * allocated so the next clip does not have to rebuild it.
 */
export function showAvPlay(): void {
  const el = surface();
  if (el) el.style.visibility = "visible";
}

export function hideAvPlay(): void {
  const el = surface();
  if (el) el.style.visibility = "hidden";
}

/* -------------------------------------------------------------------------- */
/* URLs.                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Make a URL absolute, because a native player resolves nothing.
 *
 * ⛔ **This is the difference between playing and `PLAYER_ERROR_INVALID_URI`.**
 * Foyer's timeline carries root-relative asset URLs (`/foyer-assets/x.mp4`) and
 * that has always been fine: the WGT sets `<base href="https://foyer.nenda.com/">`
 * and the browser resolves `<video src>` against it. AVPlay is not the browser.
 * It has no document, no base element and no origin - a path with no scheme is
 * not a URL to it, and it rejects the open outright.
 *
 * Cached files are unaffected: Tizen's `File.toURI()` already returns an
 * absolute `file://` URI, which is left exactly as it is.
 *
 * Resolving through `URL` also percent-encodes what needs encoding, which is
 * the other half of `INVALID_URI` - a filename with a space in it fails the
 * same way, and the legacy player called `encodeURI` for precisely that reason.
 */
export function absoluteMediaUri(src: string): string {
  if (!src) return src;
  // Already carries a scheme: http:, https:, file:, and whatever else the
  // firmware accepts. Nothing to resolve, and re-parsing risks mangling it.
  if (/^[a-z][a-z0-9+.-]*:/i.test(src)) return src;
  if (typeof document === "undefined") return src;
  try {
    return new URL(src, document.baseURI).href;
  } catch {
    // A URL that will not parse is one AVPlay would refuse anyway; hand it
    // through unchanged so the error names the real thing.
    return src;
  }
}

/* -------------------------------------------------------------------------- */
/* Geometry.                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The video's own dimensions, from the prepared stream.
 *
 * Only meaningful after `prepareAsync` has succeeded - before that the track
 * list is empty and the caller has to guess from the screen. Null rather than a
 * fallback, so the difference between "measured" and "assumed" stays visible at
 * the call site.
 */
function trackDimensions(
  avplay: AvPlayApi
): { width: number; height: number } | null {
  try {
    const video = avplay
      .getTotalTrackInfo?.()
      ?.find((t) => String(t?.type).toUpperCase() === "VIDEO");
    if (video?.width && video?.height) {
      return { width: video.width, height: video.height };
    }
  } catch {
    /* track info is unavailable on some firmware until playback starts */
  }
  return null;
}

/**
 * Do we positively know there is no audio track?
 *
 * True ONLY when the track list exists and contains no audio. Unknown counts as
 * "there might be", because the caller uses this to skip an audio toggle that
 * throws on video-only assets - and wrongly skipping it on an asset that DOES
 * have audio means a lobby gets sound it never asked for.
 */
function hasNoAudioTrack(avplay: AvPlayApi): boolean {
  try {
    const tracks = avplay.getTotalTrackInfo?.();
    if (!Array.isArray(tracks) || tracks.length === 0) return false;
    return !tracks.some((t) => String(t?.type).toUpperCase() === "AUDIO");
  } catch {
    return false;
  }
}

/**
 * Letterbox the video into the panel, rotating for a portrait screen.
 *
 * Signage panels are commonly hung portrait and the firmware does not rotate
 * for us - a 1080x1920 clip on a rotated panel renders sideways in a letterbox
 * unless both the rotation and the display rect are set explicitly.
 */
export function applyDisplayRect(
  avplay: AvPlayApi,
  video: { width: number; height: number } | null,
  screenW: number,
  screenH: number
): void {
  const portrait = screenH > screenW;
  try {
    avplay.setDisplayRotation?.(
      portrait ? "PLAYER_DISPLAY_ROTATION_90" : "PLAYER_DISPLAY_ROTATION_NONE"
    );
  } catch {
    /* absent on some firmware */
  }

  const videoW = video?.width ?? screenW;
  const videoH = video?.height ?? screenH;
  const videoAspect = videoW / videoH;
  const screenAspect = screenW / screenH;

  let renderW = screenW;
  let renderH = screenH;
  if (videoAspect > screenAspect) {
    renderH = Math.round(screenW / videoAspect);
  } else {
    renderW = Math.round(screenH * videoAspect);
  }

  try {
    avplay.setDisplayRect?.(
      Math.round((screenW - renderW) / 2),
      Math.round((screenH - renderH) / 2),
      renderW,
      renderH
    );
  } catch {
    /* nothing to do but play at whatever rect it already had */
  }
  try {
    avplay.setStreamingProperty?.(
      "PORTRAIT_MODE",
      videoH > videoW ? "TRUE" : "FALSE"
    );
  } catch {
    /* absent on some firmware */
  }
  try {
    avplay.setDisplayMethod?.("PLAYER_DISPLAY_MODE_LETTER_BOX");
  } catch {
    /* absent on some firmware */
  }
}

/* -------------------------------------------------------------------------- */
/* The preloader.                                                             */
/* -------------------------------------------------------------------------- */

type PreloadState = "idle" | "preparing" | "ready" | "error";

/**
 * A hung `prepareAsync` fires neither callback. Without a ceiling the consumer
 * waits on a promise nothing will ever settle, so give up and let it load on
 * demand instead.
 */
const PRELOAD_TIMEOUT_MS = 8_000;

let preloadSrc: string | null = null;
let preloadState: PreloadState = "idle";
let preloadTimer: ReturnType<typeof setTimeout> | null = null;

function clearPreloadTimer(): void {
  if (preloadTimer) {
    clearTimeout(preloadTimer);
    preloadTimer = null;
  }
}

/**
 * Open and prepare `src` ahead of its turn, so playback starts at `play()`.
 *
 * Only when the decoder is genuinely free. There is one surface: preloading
 * over a clip that is still playing would stop the wall to warm up its own
 * successor, which is the opposite of the point.
 */
export function preloadNext(raw: string): void {
  if (!raw) return;
  // Resolved HERE rather than at the call site, so the bookkeeping below and
  // the consumer's `preloadIsReady(src)` are comparing the same string. A
  // preload keyed on the relative form would never be claimed.
  const src = absoluteMediaUri(raw);
  const avplay = api();
  if (!avplay) return;

  if (
    preloadSrc === src &&
    (preloadState === "preparing" || preloadState === "ready")
  ) {
    return;
  }

  let state = "";
  try {
    state = avplay.getState?.() ?? "";
  } catch {
    /* an unreadable state is not an idle one */
    return;
  }
  if (!["IDLE", "NONE", "", "PAUSED"].includes(state)) return;

  try {
    avplay.stop?.();
  } catch {
    /* nothing was playing */
  }
  try {
    avplay.close?.();
  } catch {
    /* nothing was open */
  }

  preloadSrc = src;
  preloadState = "preparing";
  clearPreloadTimer();
  preloadTimer = setTimeout(() => {
    if (preloadSrc === src && preloadState === "preparing") {
      preloadState = "error";
      // Detach the src as well: a late callback must not be able to resurrect
      // this preload to "ready" after the consumer already gave up on it and
      // started its own load.
      preloadSrc = null;
    }
  }, PRELOAD_TIMEOUT_MS);

  try {
    avplay.open?.(src);
    avplay.setDisplayMethod?.("PLAYER_DISPLAY_MODE_LETTER_BOX");
    avplay.prepareAsync?.(
      () => {
        if (preloadSrc === src) {
          clearPreloadTimer();
          preloadState = "ready";
        }
      },
      () => {
        if (preloadSrc === src) {
          clearPreloadTimer();
          preloadState = "error";
        }
      }
    );
  } catch {
    clearPreloadTimer();
    preloadSrc = null;
    preloadState = "idle";
  }
}

// The three queries below resolve their argument too. Callers hold the URL in
// whatever form the timeline gave them, and a preload keyed on the absolute
// form would otherwise never be found by a caller asking with the relative one.

export function preloadIsReady(src: string): boolean {
  return preloadSrc === absoluteMediaUri(src) && preloadState === "ready";
}

export function preloadIsPreparing(src: string): boolean {
  return preloadSrc === absoluteMediaUri(src) && preloadState === "preparing";
}

/** Claim the preload, whatever its state. Called when the consumer takes over. */
export function consumePreload(): void {
  clearPreloadTimer();
  preloadSrc = null;
  preloadState = "idle";
}

export function preloadIsActive(): boolean {
  return preloadState === "preparing" || preloadState === "ready";
}

export function preloadIsActiveFor(src: string): boolean {
  return preloadIsActive() && preloadSrc === absoluteMediaUri(src);
}

/** Test seam. Resets the module's single-surface bookkeeping. */
export function resetPreloadForTests(): void {
  clearPreloadTimer();
  preloadSrc = null;
  preloadState = "idle";
}

/* -------------------------------------------------------------------------- */
/* Playing one thing.                                                         */
/* -------------------------------------------------------------------------- */

/**
 * AVPlay reports no stall of its own that can be relied on, so the playhead is
 * polled. A frozen `getCurrentTime` while the state says PLAYING is the only
 * signal there is.
 */
const STALL_WARN_MS = 3_000;
const STALL_FAIL_MS = 10_000;
/**
 * A rebuffer ALSO reports PLAYING with a frozen playhead, so buffering pauses
 * the stall clock and gets this longer ceiling instead. Without the separation
 * every slow uplink looks like a wedged decoder.
 */
const BUFFER_FAIL_MS = 30_000;
const WATCHDOG_MS = 1_000;

export interface AvPlayDrm {
  /** Where a Widevine challenge is exchanged for a licence. Already signed. */
  licenceServerUrl: string;
}

export interface AvPlayOptions {
  src: string;
  /** The next source, preloaded when this one completes. */
  nextSrc?: string;
  muted: boolean;
  /** Ask the firmware to loop. Falls back to the caller's `onEnded`. */
  loop: boolean;
  /** A DASH stream, which needs its DRM session opened before prepare. */
  isDash?: boolean;
  drm?: AvPlayDrm;
  screen: { width: number; height: number };
  startAtSeconds?: number;
  onEnded: () => void;
  /**
   * Something went wrong.
   *
   * `fatal` separates the two kinds, because the caller's response differs and
   * getting it backwards empties a playlist:
   *
   *  - **fatal** - the source will not open or play at all (bad URI, no codec,
   *    a DRM session that will not start). Skipping it is right; retrying is
   *    just a slower way to show black.
   *  - **not fatal** - it opened, it started, and then the playhead stopped.
   *    Reported so an operator can see it, but NOT a reason to strike the item
   *    out. A stall is frequently the network, and Foyer's `dead` set is
   *    permanent for the session: three stalls on a three-item loop would blank
   *    the wall until somebody reboots the panel.
   */
  onError: (message: string, fatal: boolean) => void;
  /** Non-fatal progress and stall notes, for the log ring buffer. */
  onEvent?: (message: string) => void;
}

/**
 * Start playing, and return the teardown.
 *
 * The teardown deliberately does NOT stop the player when the preloader has
 * already claimed the surface for `nextSrc` - tearing down there would close
 * the very stream that was just prepared, and the next clip would start from
 * cold every single time.
 */
export function startAvPlay(input: AvPlayOptions): () => void {
  // Both URLs are made absolute ONCE, here, and nothing below ever sees the
  // relative form. See `absoluteMediaUri` - handing AVPlay a path with no
  // scheme is PLAYER_ERROR_INVALID_URI and no playback at all.
  const opts: AvPlayOptions = {
    ...input,
    src: absoluteMediaUri(input.src),
    nextSrc: input.nextSrc ? absoluteMediaUri(input.nextSrc) : undefined,
  };

  const avplay = api();
  if (!avplay) {
    opts.onError("AVPlay is not available on this platform", true);
    return () => {};
  }

  const screenW = opts.screen.width || 1920;
  const screenH = opts.screen.height || 1080;

  let disposed = false;
  let watchdog: ReturnType<typeof setInterval> | null = null;
  let buffering = false;
  let bufferingSince = 0;

  const clearWatchdog = () => {
    if (watchdog !== null) {
      clearInterval(watchdog);
      watchdog = null;
    }
  };

  const fail = (message: string, fatal = true) => {
    if (disposed) return;
    clearWatchdog();
    hideAvPlay();
    opts.onError(message, fatal);
  };

  const startWatchdog = () => {
    clearWatchdog();
    let lastPlayhead = -1;
    let lastAdvanceAt = perf();
    let seeded = false;
    let warned = false;

    watchdog = setInterval(() => {
      let state = "";
      try {
        state = avplay.getState?.() ?? "";
      } catch {
        /* treated as "not playing" below */
      }
      // Only PLAYING is a promise that the playhead should be moving. Paused,
      // preparing and idle all legitimately stand still.
      if (state !== "PLAYING") {
        lastAdvanceAt = perf();
        warned = false;
        return;
      }
      if (buffering) {
        lastAdvanceAt = perf();
        warned = false;
        if (perf() - bufferingSince >= BUFFER_FAIL_MS) {
          fail("buffering never completed", false);
        }
        return;
      }

      let t = 0;
      try {
        t = avplay.getCurrentTime?.() ?? 0;
      } catch {
        /* an unreadable playhead is judged as frozen, which it effectively is */
      }
      // Seed on the first PLAYING poll: the position at that instant is not
      // evidence of anything yet.
      if (!seeded) {
        seeded = true;
        lastPlayhead = t;
        lastAdvanceAt = perf();
        return;
      }
      // ANY movement counts, including backwards - a loop wrapping to zero is
      // progress, and judging direction here would call every lap a stall.
      if (Math.abs(t - lastPlayhead) > 10) {
        lastPlayhead = t;
        lastAdvanceAt = perf();
        warned = false;
        return;
      }

      const stuckMs = perf() - lastAdvanceAt;
      if (stuckMs >= STALL_FAIL_MS) {
        fail(`no playback progress for ${Math.round(stuckMs)}ms`, false);
      } else if (stuckMs >= STALL_WARN_MS && !warned) {
        warned = true;
        opts.onEvent?.(
          `[avplay] stall warning: playhead frozen ${Math.round(stuckMs)}ms at ${Math.round(t)}ms`
        );
      }
    }, WATCHDOG_MS);
  };

  /** Everything that must happen once the stream is prepared, in order. */
  const startPlaying = (preloaded: boolean) => {
    if (disposed) return;
    try {
      applyDisplayRect(avplay, trackDimensions(avplay), screenW, screenH);
      if (opts.startAtSeconds && opts.startAtSeconds > 0) {
        try {
          avplay.seekTo?.(Math.round(opts.startAtSeconds * 1000));
        } catch {
          /* starting from the top is better than not starting */
        }
      }
      if (opts.loop) {
        try {
          avplay.setLooping?.(true);
        } catch {
          /* absent on some firmware - onEnded is the fallback */
        }
      }
      // Skip the toggle only when we KNOW there is no audio track: toggling a
      // video-only asset throws on some firmware, and skipping it wrongly on an
      // asset that has audio gives a lobby unexpected sound.
      try {
        if (!hasNoAudioTrack(avplay)) {
          if (opts.muted) avplay.disableAudioStream?.();
          else avplay.enableAudioStream?.();
        }
      } catch {
        /* the toggle is a preference, not a precondition */
      }
      showAvPlay();
      avplay.play?.();
      startWatchdog();
      opts.onEvent?.(
        `[avplay] playing${preloaded ? " (preloaded)" : ""} · ${short(opts.src)}`
      );
    } catch (e) {
      fail(`could not start playback: ${message(e)}`);
    }
  };

  /* -- the preloaded path ------------------------------------------------- */

  // Already open and prepared by `preloadNext`. Skip straight to play - this is
  // the whole reason the preloader exists, and re-opening here would throw away
  // the work and reintroduce the gap between clips.
  if (preloadIsReady(opts.src)) {
    consumePreload();
    startPlaying(true);
    return () => {
      disposed = true;
      clearWatchdog();
      teardown(avplay, opts.nextSrc);
    };
  }

  /* -- the cold path ------------------------------------------------------ */

  // A preload for some OTHER source is in flight over the single surface; it
  // has to be abandoned before this one can have the decoder.
  if (preloadIsActive()) consumePreload();

  try {
    avplay.stop?.();
  } catch {
    /* nothing was playing */
  }
  try {
    avplay.close?.();
  } catch {
    /* nothing was open */
  }

  /**
   * What DRM this attempt was given, for the failure line below.
   *
   * Declared out here because both `onerror` and the prepare callback need it,
   * and it is only settled inside the open sequence further down.
   */
  let drmSetup = "no drm configured";

  try {
    avplay.setListener?.({
      onbufferingstart: () => {
        buffering = true;
        bufferingSince = perf();
      },
      onbufferingcomplete: () => {
        buffering = false;
      },
      onstreamcompleted: () => {
        if (disposed) return;
        clearWatchdog();
        // Warm the next clip in the gap. There is one surface, so this is the
        // only moment a preload is possible at all.
        if (opts.nextSrc) preloadNext(opts.nextSrc);
        hideAvPlay();
        opts.onEnded();
      },
      onerror: (msg: unknown) => {
        fail(
          `AVPlay error: ${String(msg)} · ${describeAttempt(opts, drmSetup)}`
        );
      },
      ondrmevent: (_type: unknown, data: unknown) => {
        void handleDrmEvent(avplay, opts, data).catch((e: unknown) => {
          fail(`DRM: ${message(e)}`);
        });
      },
    });
  } catch (e) {
    opts.onError(`setListener failed: ${message(e)}`, true);
    return () => {
      disposed = true;
    };
  }

  try {
    // Two seconds of buffer before play. The default is longer, which on a
    // signage loop is two seconds of black at every clip change.
    try {
      avplay.setBufferingParam?.(
        "PLAYER_BUFFER_FOR_PLAY",
        "PLAYER_BUFFER_SIZE_IN_SECOND",
        2
      );
    } catch {
      /* absent on some firmware */
    }

    avplay.open?.(opts.src);

    // Park the surface off-screen until the stream is prepared and its real
    // dimensions are known. Without this the first frame lands at whatever rect
    // the previous clip left behind - a visible jump on every change.
    try {
      avplay.setDisplayRect?.(9999, 9999, 1, 1);
    } catch {
      /* absent on some firmware */
    }

    if (opts.isDash && opts.drm) {
      const drmParams = JSON.stringify({
        // Names this playback session to the CDM. Any stable-per-session value
        // works; it is not a credential and never leaves the panel.
        AppSession: String(Math.floor(Math.random() * 10000)),
        DataType: "MPEG-DASH",
      });
      const response = avplay.setDrm?.(
        "WIDEVINE_CDM",
        "SetProperties",
        drmParams
      );
      if (response !== "true") {
        opts.onError(`could not open a DRM session: ${String(response)}`, true);
        return () => {
          disposed = true;
        };
      }
      drmSetup = "widevine session open";
    }

    avplay.prepareAsync?.(
      () => startPlaying(false),
      (e: unknown) =>
        fail(
          `prepare failed: ${message(e)} · ${describeAttempt(opts, drmSetup)}`
        )
    );
  } catch (e) {
    fail(`could not open ${short(opts.src)}: ${message(e)}`);
  }

  return () => {
    disposed = true;
    clearWatchdog();
    teardown(avplay, opts.nextSrc);
  };
}

/**
 * Release the surface - unless the preloader has already claimed it.
 *
 * The exception is the whole reason this is a function. `onstreamcompleted`
 * preloads the next source, and React then unmounts this player; a teardown
 * that stopped unconditionally would close the stream that was just prepared,
 * and every clip would start cold.
 */
function teardown(avplay: AvPlayApi, nextSrc: string | undefined): void {
  if (nextSrc && preloadIsActiveFor(nextSrc)) {
    hideAvPlay();
    return;
  }
  try {
    avplay.stop?.();
  } catch {
    /* already stopped */
  }
  try {
    avplay.close?.();
  } catch {
    /* already closed */
  }
  hideAvPlay();
}

/**
 * Answer a Widevine licence challenge.
 *
 * The panel hands us an opaque challenge; we POST it to the licence server and
 * hand the response back. `licenceServerUrl` already carries its signed token -
 * see `verimatrixLicence.ts` on the server, which is the only place the signing
 * key exists. Nothing here can mint entitlement, which is deliberate: this code
 * runs on a television in a hotel.
 */
async function handleDrmEvent(
  avplay: AvPlayApi,
  opts: AvPlayOptions,
  data: unknown
): Promise<void> {
  const event = data as {
    name?: string;
    challenge?: string;
    session_id?: string;
  };
  if (event?.name !== "Challenge") return;
  if (!opts.drm) throw new Error("a DRM challenge arrived with no licence URL");
  if (!event.challenge) throw new Error("the challenge carried no payload");

  const challenge = Uint8Array.from(atob(event.challenge), (c) =>
    c.charCodeAt(0)
  );
  const res = await fetch(opts.drm.licenceServerUrl, {
    method: "POST",
    headers: { "Content-Type": "application/octet-stream" },
    body: challenge,
  });
  if (!res.ok) throw new Error(`licence request failed (${res.status})`);

  // Verimatrix answers with raw bytes; some deployments wrap it in JSON. Both
  // shapes appear in the field, so both are read rather than assumed.
  let licence: string;
  if ((res.headers.get("content-type") ?? "").startsWith("application/json")) {
    licence = ((await res.json()) as { license?: string }).license ?? "";
  } else {
    licence = base64(await res.arrayBuffer());
  }
  if (!licence) throw new Error("the licence server returned nothing usable");

  // The delimiter is Samsung's, not ours - `setDrm` parses this single string
  // back into a session id and a licence.
  avplay.setDrm?.(
    "WIDEVINE_CDM",
    "widevine_license_data",
    `${event.session_id ?? ""}PARAM_START_POSITION${licence}PARAM_START_POSITION`
  );
}

/* -------------------------------------------------------------------------- */

function perf(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

function message(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (e && typeof e === "object") {
    try {
      return JSON.stringify(e);
    } catch {
      return String(e);
    }
  }
  return String(e);
}

/**
 * What the player was ASKED to do, appended to a failure.
 *
 * ⛔ AVPlay answers `PLAYER_ERROR_NOT_SUPPORTED_FORMAT` to two completely
 * different questions: a codec or container it will not decode, and encrypted
 * samples it has no key system for. The remedies are opposite - one is a
 * conversation with the packager, the other is a bug in the licence path one
 * layer up - and the verdict alone cannot separate them. What the CALLER
 * configured can, and this module is the only place that knows it.
 *
 * `dash · no drm configured` is the pair worth reading twice: a manifest whose
 * samples are encrypted, handed to a decoder that was told nothing about DRM,
 * fails here and looks for all the world like a codec problem.
 *
 * ⚠️ The DRM SYSTEM is named, never the licence URL. That URL is an ES256 JWT
 * riding in a query string, and this string travels the heartbeat into the CMS,
 * onto a fleet row and from there into tickets and screenshots.
 */
function describeAttempt(opts: AvPlayOptions, drmSetup: string): string {
  return `${short(opts.src)} · ${opts.isDash ? "dash" : "file"} · ${drmSetup}`;
}

/** A URL short enough for a log line that shares a ring buffer with everything else. */
function short(src: string): string {
  const name = src.split("/").pop()?.split("?")[0] ?? src;
  return name.length > 48 ? `${name.slice(0, 48)}…` : name;
}

/**
 * Base64 without blowing the stack.
 *
 * `String.fromCharCode(...bytes)` on a licence of any size exceeds the argument
 * limit and throws a RangeError - which would surface as "DRM failed" on a
 * panel and send someone hunting for a key problem that does not exist.
 */
function base64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}
